<template>
  <div>
    <!-- ==================== Hero ==================== -->
    <section class="hero">
      <div class="container">
        <h1>{{ $t('features.heroTitle') }}</h1>
        <p>{{ $t('features.heroSubtitle') }}</p>
      </div>
    </section>

    <!-- ==================== Feature Cards ==================== -->
    <section class="features-list">
      <div class="container">
        <div class="feature-item" v-for="(f, i) in features" :key="i" :class="{ reverse: i % 2 === 1 }">
          <div class="feature-visual">
            <div class="feature-icon-large" :style="{ background: f.gradient }">
              <span v-html="f.icon"></span>
            </div>
          </div>
          <div class="feature-text">
            <h3>{{ f.title }}</h3>
            <p class="feature-desc">{{ f.desc }}</p>
            <div class="feature-highlight">{{ f.highlight }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== CTA ==================== -->
    <section class="cta">
      <div class="container">
        <h2>{{ $t('features.cta') }}</h2>
        <p>{{ $t('features.ctaDesc') }}</p>
        <router-link to="/download" class="btn btn-primary btn-lg">
          <span>⬇</span> {{ $t('home.download') }}
        </router-link>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Features',
  computed: {
    features() {
      return [
      {
        title: this.$t('features.agentEngine'),
        desc: this.$t('features.agentEngineDesc'),
        highlight: this.$t('features.agentEngineHighlight'),
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        icon: '<img src="/img/agent-factory.svg" alt="Agent Factory" style="width:100%;height:100%;object-fit:contain;padding:10px">'
      },
      {
        title: this.$t('features.rag'),
        desc: this.$t('features.ragDesc'),
        highlight: this.$t('features.ragHighlight'),
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        icon: '📚'
      },
      {
        title: this.$t('features.notes'),
        desc: this.$t('features.notesDesc'),
        highlight: this.$t('features.notesHighlight'),
        gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        icon: '📝'
      },
      {
        title: this.$t('features.multimodal'),
        desc: this.$t('features.multimodalDesc'),
        highlight: this.$t('features.multimodalHighlight'),
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        icon: '🎨'
      },
      {
        title: this.$t('features.mcp'),
        desc: this.$t('features.mcpDesc'),
        highlight: this.$t('features.mcpHighlight'),
        gradient: 'linear-gradient(135deg, #29c840 0%, #20a0ff 100%)',
        icon: '🔌'
      },
      {
        title: this.$t('features.security'),
        desc: this.$t('features.securityDesc'),
        highlight: this.$t('features.securityHighlight'),
        gradient: 'linear-gradient(135deg, #141e30 0%, #243b55 100%)',
        icon: '🔒'
      },
      {
        title: this.$t('features.deploy'),
        desc: this.$t('features.deployDesc'),
        highlight: this.$t('features.deployHighlight'),
        gradient: 'linear-gradient(135deg, #ff9a44 0%, #ff6b6b 100%)',
        icon: '⚙️'
      },
      {
        title: this.$t('features.api'),
        desc: this.$t('features.apiDesc'),
        highlight: this.$t('features.apiHighlight'),
        gradient: 'linear-gradient(135deg, #00b4db 0%, #0083b0 100%)',
        icon: '🔗'
      }
    ]
    }
  }
}
</script>

<style scoped>
/* Hero */
.hero {
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: white;
  padding: 100px 20px;
  text-align: center;
}

.hero h1 {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 16px;
}

.hero p {
  font-size: 1.2rem;
  color: #a0a0cc;
  max-width: 600px;
  margin: 0 auto;
}

/* Feature Items */
.features-list {
  padding: 80px 20px;
  background: white;
}

.feature-item {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  padding: 80px 0;
  border-bottom: 1px solid #f0f0f0;
}

.feature-item:last-child {
  border-bottom: none;
}

.feature-item.reverse .feature-visual {
  order: 2;
}

.feature-item.reverse .feature-text {
  order: 1;
}

.feature-icon-large {
  width: 100%;
  max-width: 320px;
  aspect-ratio: 4 / 3;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 5rem;
  margin: 0 auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.1);
}

.feature-text h3 {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: #222;
}

.feature-desc {
  color: #555;
  line-height: 1.8;
  font-size: 1.05rem;
  margin-bottom: 24px;
}

.feature-highlight {
  display: inline-block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
}

/* CTA */
.cta {
  padding: 100px 20px;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: white;
  text-align: center;
}

.cta h2 {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 16px;
}

.cta p {
  font-size: 1.1rem;
  color: #a0a0cc;
  margin-bottom: 40px;
}

.btn-lg {
  padding: 14px 36px;
  font-size: 1.1rem;
}

/* Responsive */
@media (max-width: 768px) {
  .hero h1 {
    font-size: 2rem;
  }

  .feature-item,
  .feature-item.reverse {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 50px 0;
  }

  .feature-item.reverse .feature-visual {
    order: 1;
  }

  .feature-item.reverse .feature-text {
    order: 2;
  }

  .feature-text h3 {
    font-size: 1.4rem;
  }

  .feature-icon-large {
    max-width: 200px;
    font-size: 3.5rem;
  }
}
</style>
